import type { HotelResultsProps, HotelResultsShare } from '@voll-intelligence/intelligence-ui-kit';
import type {
  GenUiTranslate,
  HotelPlannerBlock,
  HotelPlannerSelection,
} from '@voll-intelligence/genui-contract';
import type { GenUiPorts } from '../../../shared/ui/config.js';
import type { createHotelPlannerView } from './hotel_planner_view.js';
import type { HotelStage } from './hotel_selection.js';

/** O compartilhar do rodapé do card (#5156) — só com o port do host. O PDF leva
 *  as ofertas que o dock mostra; o link, o bloco da busca em cena. */
export function dockShareOf({
  ports,
  ui,
  t,
  locks,
}: {
  readonly ports: Pick<GenUiPorts, 'hotelSearchShare'> | undefined;
  readonly ui: HotelPlannerBlock;
  readonly t: GenUiTranslate;
  /** `searchLocked` do card: só de leitura, busca vencida (`expiresAt`) ou em
   *  voo. Os preços em cena podem não valer mais, e o PDF sairia com o carimbo de
   *  agora — então o PDF fica desabilitado e só o link (que refaz a busca) vale. */
  readonly locks: { readonly searchLocked: boolean };
}): HotelResultsShare | undefined {
  const port = ports?.hotelSearchShare;
  if (!port) return undefined;
  return {
    copy: {
      label: t('hotel.results.share'),
      copyLinkLabel: t('searchShare.copyLink'),
      downloadPdfLabel: t('searchShare.downloadPdf'),
    },
    onCopyLink: () => {
      port.copyLink(ui);
    },
    onDownloadPdf: (offers) => {
      port.downloadPdf(ui, offers);
    },
    ...(locks.searchLocked ? { pdfDisabled: true } : {}),
  };
}

/** Monta as props do dock compacto (`HotelResults`) — fora de
 *  `HotelResultsOrganism` só para caber no teto de linhas; sem lógica própria
 *  além da montagem. */
export function sharedResultsProps({
  notice,
  selection,
  view,
  stage,
  ui,
  search,
  locked,
  busy,
  dockOfferLimit,
  share,
  onRetry,
  onHotelSelect,
  onOpenDetails,
}: {
  readonly notice: Pick<HotelResultsProps, 'state' | 'message' | 'noticeTitle' | 'noticeTone'>;
  readonly selection: HotelPlannerSelection | null;
  readonly view: ReturnType<typeof createHotelPlannerView>;
  readonly stage: HotelStage;
  readonly ui: HotelPlannerBlock;
  readonly search: HotelResultsProps['search'];
  readonly locked: boolean;
  readonly busy: boolean;
  /** Corte do DOCK e dos pinos (ramo `compact` da união): `HotelResults` deriva
   *  os dois da MESMA lista, então cortar aqui corta os dois juntos. Nunca
   *  chega ao portal — ele lê `view`/`list` direto. */
  readonly dockOfferLimit: number | undefined;
  readonly share: HotelResultsShare | undefined;
  readonly onRetry: HotelResultsProps['onRetry'];
  readonly onHotelSelect: HotelResultsProps['onHotelSelect'];
  readonly onOpenDetails: HotelResultsProps['onDetailsOpen'];
}): Omit<HotelResultsProps, 'onMapExpand' | 'onSeeAll'> {
  const dockViews =
    dockOfferLimit === undefined ? view.offers : view.offers.slice(0, dockOfferLimit);
  return {
    ...notice,
    // Sem seleção completa não há o que re-buscar: `retrySearch` sairia calado.
    ...(selection === null ? { retryUnavailable: true } : {}),
    ...(share ? { share } : {}),
    copy: view.resultsCopy,
    // Carregando não carrega lista velha: o dock esconde os cards da busca
    // anterior e o mapa fica só com a âncora do destino da vez — que é o que
    // deixa a câmera ir até ele em vez de ficar parada na cidade anterior. O
    // bloco segue com as ofertas (a gaveta aberta lê de lá).
    offers: stage === 'loading' ? [] : dockViews,
    selectedHotelId: ui.selectedOfferId,
    map: view.map,
    search,
    readOnly: locked,
    busy,
    onRetry,
    onHotelSelect,
    onDetailsOpen: onOpenDetails,
  };
}
